import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone,
  TrendingUp,
  Flame,
  RefreshCw,
  BarChart3,
} from 'lucide-react';
import type { User, Call, Stats, CallFilters, SalesPhase } from '../types';
import { api } from '../api/client';
import Sidebar, { type TabId } from '../components/Sidebar';
import Filters from '../components/Filters';
import StatsCard from '../components/StatsCard';
import PhaseChart from '../components/PhaseChart';
import LeadScoreDistribution from '../components/LeadScoreDistribution';
import CallList from '../components/CallList';
import CallDetailModal from '../components/CallDetailModal';
import SEEDocuments from '../components/SEEDocuments';
import Transcripts from '../components/Transcripts';
import SyncDashboard from '../components/SyncDashboard';
import DataImport from '../components/DataImport';

interface DashboardProps {
  user: User | null;
  onLogout: () => void;
}

const defaultPhases: Record<SalesPhase, number> = {
  erstkontakt: 0,
  qualifikation: 0,
  angebot: 0,
  verhandlung: 0,
  abschluss: 0,
  nachbearbeitung: 0,
};

export default function Dashboard({ user, onLogout }: DashboardProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [currentUser, setCurrentUser] = useState<User | null>(user);

  // Data state
  const [calls, setCalls] = useState<Call[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [filters, setFilters] = useState<CallFilters>({});
  const [loading, setLoading] = useState(true);
  const [totalCalls, setTotalCalls] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  // Modal state
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);

  const LIMIT = 50;

  // Load user data if not passed
  useEffect(() => {
    const loadUser = async () => {
      if (!currentUser) {
        try {
          const userData = await api.getMe();
          setCurrentUser(userData);
        } catch {
          navigate('/login');
        }
      }
    };
    loadUser();
  }, [currentUser, navigate]);

  // Load calls
  const loadCalls = useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const newOffset = reset ? 0 : offset;
        const response = await api.getCalls(filters, LIMIT, newOffset);

        if (reset) {
          setCalls(response.data);
        } else {
          setCalls((prev) => [...prev, ...response.data]);
        }

        setTotalCalls(response.total);
        setHasMore(response.hasMore);
        if (reset) {
          setOffset(LIMIT);
        } else {
          setOffset((prev) => prev + LIMIT);
        }
      } catch (err) {
        console.error('Failed to load calls:', err);
      } finally {
        setLoading(false);
      }
    },
    [filters, offset]
  );

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const data = await api.getStats(filters);
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, [filters]);

  // Initial load
  useEffect(() => {
    loadCalls(true);
    loadStats();
  }, [filters]);

  const handleFiltersChange = (newFilters: CallFilters) => {
    setFilters(newFilters);
    setOffset(0);
  };

  const handleRefresh = () => {
    loadCalls(true);
    loadStats();
  };

  const handleLoadMore = () => {
    loadCalls(false);
  };

  const handleCallClick = (call: Call) => {
    setSelectedCall(call);
  };

  const handleCloseModal = () => {
    setSelectedCall(null);
  };

  const handleModalRefresh = async () => {
    if (selectedCall) {
      try {
        const updatedCall = await api.getCall(selectedCall.id);
        setSelectedCall(updatedCall);
        // Also refresh the list
        loadCalls(true);
      } catch (err) {
        console.error('Failed to refresh call:', err);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch {
      // Ignore errors
    }
    onLogout();
    navigate('/login');
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="p-6 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatsCard
                title="Anrufe gesamt"
                value={stats?.total_calls ?? 0}
                icon={Phone}
                color="blue"
              />
              <StatsCard
                title="Durchschn. Leadprioritat"
                value={stats?.avg_lead_score?.toFixed(1) ?? '-'}
                icon={TrendingUp}
                color="green"
              />
              <StatsCard
                title="Gesamtscore"
                value={
                  stats
                    ? (stats.hot_leads * 30 + stats.warm_leads * 10 - stats.cold_leads * 10).toString()
                    : '-'
                }
                icon={TrendingUp}
                color="purple"
              />
              <StatsCard
                title="Heisse Leads"
                value={stats?.hot_leads ?? 0}
                icon={Flame}
                color="red"
              />
              <StatsCard
                title="HubSpot Sync"
                value={stats?.synced_to_hubspot ?? 0}
                icon={RefreshCw}
                color="green"
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PhaseChart data={stats?.calls_by_phase ?? defaultPhases} />
              <LeadScoreDistribution
                hot={stats?.hot_leads ?? 0}
                warm={stats?.warm_leads ?? 0}
                cold={stats?.cold_leads ?? 0}
              />
            </div>

            {/* Recent Calls */}
            <CallList
              calls={calls.slice(0, 10)}
              loading={loading}
              filters={filters}
              total={totalCalls}
              hasMore={false}
              onLoadMore={() => setActiveTab('calls')}
              onCallClick={handleCallClick}
              onRefresh={handleRefresh}
              compact
            />
          </div>
        );

      case 'calls':
        return (
          <div className="p-6">
            <CallList
              calls={calls}
              loading={loading}
              filters={filters}
              total={totalCalls}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              onCallClick={handleCallClick}
              onRefresh={handleRefresh}
            />
          </div>
        );

      case 'documents':
        return (
          <SEEDocuments
            calls={calls}
            loading={loading}
            onRefresh={handleRefresh}
            onCallClick={handleCallClick}
          />
        );

      case 'transcripts':
        return (
          <Transcripts
            calls={calls}
            loading={loading}
            onRefresh={handleRefresh}
            onCallClick={handleCallClick}
          />
        );

      case 'analytics':
        return (
          <div className="p-6">
            <div className="card p-8 text-center">
              <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                Analysen
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Detaillierte Analysen kommen in Phase 3
              </p>
            </div>
          </div>
        );

      case 'sync':
        return <SyncDashboard />;

      case 'data-import':
        return <DataImport />;

      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        user={currentUser}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Filters */}
        {(activeTab === 'overview' ||
          activeTab === 'calls' ||
          activeTab === 'documents' ||
          activeTab === 'transcripts') && (
          <Filters filters={filters} onFiltersChange={handleFiltersChange} />
        )}

        {/* Content */}
        <main className="flex-1 overflow-y-auto">{renderTabContent()}</main>
      </div>

      {/* Call Detail Modal */}
      {selectedCall && (
        <CallDetailModal
          call={selectedCall}
          onClose={handleCloseModal}
          onRefresh={handleModalRefresh}
        />
      )}
    </div>
  );
}
